import React, { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Link2, Check, X, RefreshCw, Plus, ExternalLink, Loader2, ChevronDown, ChevronUp,
  AlertTriangle, Code2, Eye, Undo2
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_COLORS = {
  service: 'bg-blue-100 text-blue-700',
  pillar: 'bg-purple-100 text-purple-700',
  cluster: 'bg-teal-100 text-teal-700',
  conversion: 'bg-orange-100 text-orange-700',
  manual: 'bg-gray-100 text-gray-700',
};

const ScoreBar = ({ score }) => (
  <div className="flex items-center gap-1.5">
    <div className="w-12 h-1.5 bg-gray-200 rounded-full overflow-hidden">
      <div className={`h-full rounded-full ${score >= 70 ? 'bg-green-500' : score >= 50 ? 'bg-yellow-500' : 'bg-red-400'}`}
        style={{ width: `${score}%` }} />
    </div>
    <span className="text-[10px] text-gray-500 tabular-nums">{score}</span>
  </div>
);

export const InternalLinksPanel = ({ blogId, blogData }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [approved, setApproved] = useState([]);
  const [rejected, setRejected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [showRejected, setShowRejected] = useState(false);
  const [manualForm, setManualForm] = useState({ anchor_text: '', target_url: '', link_type: 'manual', reason: '' });

  // Insertion state
  const [insertionPreview, setInsertionPreview] = useState(null);
  const [insertionStatus, setInsertionStatus] = useState(null); // null | 'previewing' | 'inserting' | 'inserted' | 'rolling_back'
  const [insertionResult, setInsertionResult] = useState(null);
  const [hasSnapshot, setHasSnapshot] = useState(false);

  const generateSuggestions = useCallback(async () => {
    setLoading(true);
    try {
      let res;
      if (blogId) {
        res = await fetch(`${BACKEND_URL}/api/internal-links/suggest/${blogId}`, { method: 'POST' });
      } else {
        res = await fetch(`${BACKEND_URL}/api/internal-links/suggest-by-content`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(blogData || {}),
        });
      }
      if (res.ok) {
        const data = await res.json();
        setSuggestions(data.suggestions || []);
        toast.success(`Generated ${data.count} link suggestions`);
      } else {
        toast.error('Failed to generate suggestions');
      }
    } catch { toast.error('Network error'); }
    setLoading(false);
  }, [blogId, blogData]);

  const loadExisting = useCallback(async () => {
    if (!blogId) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/internal-links/${blogId}`);
      if (res.ok) {
        const data = await res.json();
        const links = data.internalLinks || {};
        setSuggestions(links.suggestions || []);
        setApproved(links.approved || []);
        setRejected(links.rejected || []);
        setHasSnapshot(!!links.content_snapshot);
        if (links.inserted && links.inserted.length > 0) {
          setInsertionResult({ inserted: links.inserted, skipped: links.skipped || [] });
        }
      }
    } catch (err) { console.error('Failed to load existing links:', err); }
  }, [blogId]);

  React.useEffect(() => { loadExisting(); }, [loadExisting]);

  const handleAction = async (linkId, action) => {
    if (!blogId) {
      const sug = suggestions.find(s => s.link_id === linkId);
      if (!sug) return;
      setSuggestions(prev => prev.filter(s => s.link_id !== linkId));
      if (action === 'approve') setApproved(prev => [...prev, { ...sug, approved_at: new Date().toISOString() }]);
      else setRejected(prev => [...prev, { ...sug, rejected_at: new Date().toISOString() }]);
      return;
    }

    try {
      const res = await fetch(`${BACKEND_URL}/api/internal-links/${blogId}/action`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ actions: [{ link_id: linkId, action }] }),
      });
      if (res.ok) {
        const sug = suggestions.find(s => s.link_id === linkId);
        setSuggestions(prev => prev.filter(s => s.link_id !== linkId));
        if (action === 'approve' && sug) setApproved(prev => [...prev, { ...sug, approved_at: new Date().toISOString() }]);
        if (action === 'reject' && sug) setRejected(prev => [...prev, { ...sug, rejected_at: new Date().toISOString() }]);
      }
    } catch { toast.error('Action failed'); }
  };

  const handleManualAdd = async () => {
    if (!manualForm.anchor_text || !manualForm.target_url) {
      toast.error('Anchor text and URL required'); return;
    }
    if (!blogId) {
      const link = { ...manualForm, link_id: Date.now().toString(), target_title: manualForm.anchor_text, relevance_score: 100, cluster: '', approved_at: new Date().toISOString() };
      setApproved(prev => [...prev, link]);
      setManualForm({ anchor_text: '', target_url: '', link_type: 'manual', reason: '' });
      setShowManual(false);
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/internal-links/${blogId}/manual`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(manualForm),
      });
      if (res.ok) {
        const data = await res.json();
        setApproved(prev => [...prev, data.link]);
        setManualForm({ anchor_text: '', target_url: '', link_type: 'manual', reason: '' });
        setShowManual(false);
        toast.success('Manual link added');
      }
    } catch { toast.error('Failed to add link'); }
  };

  // ─── Insertion Actions ───
  const previewInsertion = async () => {
    if (!blogId) return;
    setInsertionStatus('previewing');
    try {
      const res = await fetch(`${BACKEND_URL}/api/internal-links/insert/${blogId}/preview`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setInsertionPreview(data);
        setInsertionStatus(null);
      } else { toast.error('Preview failed'); setInsertionStatus(null); }
    } catch { toast.error('Preview error'); setInsertionStatus(null); }
  };

  const applyInsertion = async () => {
    if (!blogId) return;
    setInsertionStatus('inserting');
    try {
      const res = await fetch(`${BACKEND_URL}/api/internal-links/insert/${blogId}/apply`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setInsertionResult({ inserted: data.inserted_links || [], skipped: data.skipped_links || [] });
        setInsertionPreview(null);
        setHasSnapshot(true);
        setInsertionStatus('inserted');
        toast.success(`${data.inserted} links inserted into HTML!`);
      } else { toast.error('Insertion failed'); setInsertionStatus(null); }
    } catch { toast.error('Insertion error'); setInsertionStatus(null); }
  };

  const rollbackInsertion = async () => {
    if (!blogId) return;
    setInsertionStatus('rolling_back');
    try {
      const res = await fetch(`${BACKEND_URL}/api/internal-links/insert/${blogId}/rollback`, { method: 'POST' });
      if (res.ok) {
        setInsertionResult(null);
        setInsertionPreview(null);
        setHasSnapshot(false);
        setInsertionStatus(null);
        toast.success('Content rolled back to pre-insertion state');
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || 'Rollback failed');
        setInsertionStatus(null);
      }
    } catch { toast.error('Rollback error'); setInsertionStatus(null); }
  };

  const totalApproved = approved.length;
  const hasMinimum = totalApproved >= 2;
  const isInsertBusy = ['previewing', 'inserting', 'rolling_back'].includes(insertionStatus);

  return (
    <Card data-testid="internal-links-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Link2 className="w-4 h-4 text-emerald-600" /> Internal Links
          </CardTitle>
          <div className="flex items-center gap-1">
            <Badge className={hasMinimum ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'} data-testid="links-count-badge">
              {totalApproved} approved
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button size="sm" onClick={generateSuggestions} disabled={loading}
            className="bg-emerald-600 hover:bg-emerald-700 flex-1" data-testid="generate-links-btn">
            {loading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
            {suggestions.length > 0 ? 'Regenerate' : 'Suggest Links'}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setShowManual(!showManual)} data-testid="manual-link-btn">
            <Plus className="w-3.5 h-3.5" />
          </Button>
        </div>

        {/* Minimum links warning */}
        {!hasMinimum && (totalApproved > 0 || suggestions.length > 0) && (
          <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700" data-testid="min-links-warning">
            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
            Need at least 2 approved links ({totalApproved}/2)
          </div>
        )}

        {/* Manual Add Form */}
        {showManual && (
          <div className="p-3 bg-gray-50 rounded-lg space-y-2" data-testid="manual-link-form">
            <Input placeholder="Anchor text *" value={manualForm.anchor_text}
              onChange={e => setManualForm(f => ({ ...f, anchor_text: e.target.value }))} className="text-xs h-8" data-testid="manual-anchor" />
            <Input placeholder="Target URL * (e.g. /fdi)" value={manualForm.target_url}
              onChange={e => setManualForm(f => ({ ...f, target_url: e.target.value }))} className="text-xs h-8" data-testid="manual-url" />
            <div className="flex gap-2">
              <select className="text-xs border rounded px-2 py-1 flex-1" value={manualForm.link_type}
                onChange={e => setManualForm(f => ({ ...f, link_type: e.target.value }))} data-testid="manual-type">
                <option value="service">Service</option>
                <option value="pillar">Pillar</option>
                <option value="cluster">Cluster</option>
                <option value="conversion">Conversion</option>
                <option value="manual">Manual</option>
              </select>
              <Button size="sm" onClick={handleManualAdd} className="h-7 text-xs bg-emerald-600" data-testid="manual-add-submit">Add</Button>
              <Button size="sm" variant="ghost" onClick={() => setShowManual(false)} className="h-7 text-xs">Cancel</Button>
            </div>
          </div>
        )}

        {/* Pending Suggestions */}
        {suggestions.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Suggestions ({suggestions.length})</p>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {suggestions.map(s => (
                <div key={s.link_id} className="p-2.5 bg-white border rounded-lg" data-testid={`suggestion-${s.link_id}`}>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <Badge className={`text-[10px] px-1.5 py-0 ${TYPE_COLORS[s.link_type] || TYPE_COLORS.manual}`}>{s.link_type}</Badge>
                        <ScoreBar score={s.relevance_score} />
                      </div>
                      <p className="text-xs font-medium text-gray-900 truncate" title={s.anchor_text}>"{s.anchor_text}"</p>
                      <p className="text-[10px] text-gray-400 truncate flex items-center gap-1">
                        <ExternalLink className="w-2.5 h-2.5" /> {s.target_url}
                      </p>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-green-600 hover:bg-green-50"
                        onClick={() => handleAction(s.link_id, 'approve')} data-testid={`approve-${s.link_id}`}>
                        <Check className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-500 hover:bg-red-50"
                        onClick={() => handleAction(s.link_id, 'reject')} data-testid={`reject-${s.link_id}`}>
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-[10px] text-gray-500 mt-1">{s.reason}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Approved Links */}
        {approved.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-green-600 uppercase mb-2">Approved ({approved.length})</p>
            <div className="space-y-1.5">
              {approved.map(l => (
                <div key={l.link_id} className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded text-xs" data-testid={`approved-${l.link_id}`}>
                  <Check className="w-3 h-3 text-green-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="font-medium text-green-800 truncate block">"{l.anchor_text}"</span>
                    <span className="text-green-600 truncate block text-[10px]">{l.target_url}</span>
                  </div>
                  <Badge className={`text-[10px] px-1.5 py-0 ${TYPE_COLORS[l.link_type] || TYPE_COLORS.manual}`}>{l.link_type}</Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ─── Insertion Controls (only for saved blogs) ─── */}
        {blogId && approved.length > 0 && (
          <div className="border-t pt-3 space-y-3" data-testid="insertion-controls">
            <p className="text-xs font-semibold text-blue-600 uppercase flex items-center gap-1">
              <Code2 className="w-3.5 h-3.5" /> Insert into HTML
            </p>

            {/* Insertion result from previous insertion */}
            {insertionResult && !insertionPreview && (
              <div className="p-2.5 bg-blue-50 border border-blue-200 rounded-lg text-xs space-y-1" data-testid="insertion-result">
                <p className="font-semibold text-blue-700">
                  {insertionResult.inserted?.length || 0} links live in HTML
                  {insertionResult.skipped?.length > 0 && <span className="text-gray-400 font-normal ml-1">({insertionResult.skipped.length} skipped)</span>}
                </p>
                {(insertionResult.inserted || []).slice(0, 3).map((lk, i) => (
                  <p key={lk.anchor_text || `link-${i}`} className="text-blue-600 truncate">"{lk.anchor_text}" &rarr; {lk.target_url}</p>
                ))}
                {(insertionResult.inserted || []).length > 3 && (
                  <p className="text-blue-400">+{insertionResult.inserted.length - 3} more</p>
                )}
              </div>
            )}

            {/* Preview panel */}
            {insertionPreview && (
              <div className="p-2.5 bg-gray-50 border rounded-lg text-xs space-y-2" data-testid="insertion-preview">
                <div className="flex items-center justify-between">
                  <p className="font-semibold text-gray-700">Preview: {insertionPreview.pending_count} pending links</p>
                  <Button size="sm" variant="ghost" className="h-5 text-[10px]" onClick={() => setInsertionPreview(null)}>
                    <X className="w-3 h-3" />
                  </Button>
                </div>
                {insertionPreview.preview_inserted?.length > 0 && (
                  <div>
                    <p className="text-green-600 font-medium mb-1">Would Insert ({insertionPreview.preview_inserted.length})</p>
                    {insertionPreview.preview_inserted.map((lk, i) => (
                      <p key={lk.anchor_text || `ins-${i}`} className="text-gray-600 py-0.5 truncate">"{lk.anchor_text}" &rarr; {lk.target_url}</p>
                    ))}
                  </div>
                )}
                {insertionPreview.preview_skipped?.length > 0 && (
                  <div>
                    <p className="text-gray-400 font-medium mb-1">Would Skip ({insertionPreview.preview_skipped.length})</p>
                    {insertionPreview.preview_skipped.map((lk, i) => (
                      <p key={lk.anchor_text || `skip-${i}`} className="text-gray-400 py-0.5 truncate">"{lk.anchor_text}" &mdash; {lk.reason}</p>
                    ))}
                  </div>
                )}
                {insertionPreview.pending_count === 0 && (
                  <p className="text-gray-400 py-2 text-center">All approved links are already inserted. Nothing to do!</p>
                )}
              </div>
            )}

            {/* Action buttons */}
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={previewInsertion} disabled={isInsertBusy}
                className="flex-1 text-xs border-blue-300 text-blue-600 hover:bg-blue-50" data-testid="preview-insertion-btn">
                {insertionStatus === 'previewing' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Eye className="w-3 h-3 mr-1" />}
                Preview
              </Button>
              <Button size="sm" onClick={applyInsertion} disabled={isInsertBusy}
                className="flex-1 text-xs bg-blue-600 hover:bg-blue-700" data-testid="apply-insertion-btn">
                {insertionStatus === 'inserting' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Code2 className="w-3 h-3 mr-1" />}
                Apply to HTML
              </Button>
              {hasSnapshot && (
                <Button size="sm" variant="outline" onClick={rollbackInsertion} disabled={isInsertBusy}
                  className="text-xs border-red-300 text-red-600 hover:bg-red-50" data-testid="rollback-insertion-btn">
                  {insertionStatus === 'rolling_back' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Undo2 className="w-3 h-3 mr-1" />}
                  Rollback
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Rejected (collapsed) */}
        {rejected.length > 0 && (
          <div>
            <button className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600" onClick={() => setShowRejected(!showRejected)} data-testid="toggle-rejected">
              {showRejected ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              Rejected ({rejected.length})
            </button>
            {showRejected && (
              <div className="space-y-1 mt-1">
                {rejected.map(l => (
                  <div key={l.link_id} className="flex items-center gap-2 p-1.5 bg-red-50 rounded text-[10px] text-red-400 line-through">
                    "{l.anchor_text}" &rarr; {l.target_url}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Empty state */}
        {suggestions.length === 0 && approved.length === 0 && (
          <p className="text-xs text-gray-400 text-center py-3" data-testid="links-empty-state">
            Click "Suggest Links" to generate internal link recommendations
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default InternalLinksPanel;
